import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Collapse, Space, Typography } from "../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../shared/frontend/components/DetailList";
import { downloadBlob } from "../../../../../../shared/frontend/utils/fileDownloads";
import { useAuth } from "../../app/auth/useAuth";
import { Permissions } from "../../app/constants/permissions";
import { CONTRACT_STATUS_CANCELLED, CONTRACT_STATUS_ENDED } from "../../app/constants/contractStatus";
import { useI18n } from "../../app/i18n/useI18n";
import { formatDateOnly, formatDateTimeNoSeconds } from "../../app/units/format";
import { loadUnits } from "../../app/units/unitsStorage";
import type { SendDocumentReviewRequest } from "../communication/SendDocumentReviewWorkspace";
import { FormDrawer } from "../FormDrawer";
import { CancelContractDrawer, type ContractPackageForCancel } from "./CancelContractDrawer";
import { RestPeriodDrawer, type RestPeriodRequestDraft } from "./RestPeriodDrawer";

const RELEVANCE_RELEVANT = "relevant";
const PROCESSING_STATUS_QUEUED = "queued";
const PROCESSING_STATUS_PROCESSING = "processing";

export type EmailContractAction = "cancellation" | "rest_period";
type EmailRequest = {
  id: string; updatedAt: string; receivedAt: string; subject: string; bodyText: string;
  fromEmail: string; personId?: string | null; personDisplayName?: string | null;
  contractPackageId?: string | null; contractPackageDisplayName?: string | null;
  effectiveRelevance?: string | null; effectiveIntentTypes: string[]; processingStatus: string;
  requestedCancellationDate?: string | null; cancellationReasonId?: string | null;
  restPeriodStart?: string | null; restPeriodEnd?: string | null; restPeriodReasonId?: string | null;
  note?: string | null;
  existingRestPeriodIds: string[];
  restPeriodDraft?: RestPeriodRequestDraft | null;
  attachments: Array<{ id: string; fileName: string }>;
  contractProcessings: Array<{ action: EmailContractAction; personId: string; contractPackageId: string; restPeriodId?: string | null; completedAt: string }>;
};
type Person = { id: string; email?: string | null; homeStudioId?: string | null };
type Loaded = { email: EmailRequest; person: Person | null; contract: ContractPackageForCancel | null };

/** Derselbe Einstieg aus Aufgaben und Posteingang; die Fachformulare und der Versand bleiben unverändert. */
export function InboundEmailContractRequestDrawer({ emailId, action, onClose, onSuccess, onOpenDocumentReview }: {
  emailId: string; action: EmailContractAction; onClose: () => void; onSuccess: () => void;
  onOpenDocumentReview: (request: SendDocumentReviewRequest) => void;
}) {
  const auth = useAuth();
  const { text } = useI18n();
  const navigate = useNavigate();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const [loaded, setLoaded] = React.useState<Loaded | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [linking, setLinking] = React.useState(false);
  const [linkFailed, setLinkFailed] = React.useState(false);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    void (async () => {
      const email = await auth.api.get<EmailRequest>(`/api/v1/inbound-emails/${encodeURIComponent(emailId)}`);
      let person: Person | null = null;
      let contract: ContractPackageForCancel | null = null;
      if (email.personId && email.contractPackageId && !email.contractProcessings.some(x => x.action === action)) {
        const [p, packages] = await Promise.all([
          auth.api.get<Person>(`/api/v1/persons/${encodeURIComponent(email.personId)}`),
          auth.api.get<Array<ContractPackageForCancel & { effectiveMembershipEndDate?: string; primaryContractEndDate?: string }>>(
            `/api/v1/contract-packages?personId=${encodeURIComponent(email.personId)}`),
        ]);
        person = p;
        const selected = packages.find(x => x.id === email.contractPackageId);
        if (selected) contract = { ...selected, endDate: selected.effectiveMembershipEndDate ?? selected.primaryContractEndDate };
      }
      if (alive) setLoaded({ email, person, contract });
    })().catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [auth.api, emailId, action]);

  const email = loaded?.email;
  const completed = email?.contractProcessings.find(x => x.action === action);
  const restPrefill = React.useMemo(() => email ? {
    contractPackageId: email.contractPackageId ?? undefined,
    startDate: email.restPeriodStart ?? undefined, endDate: email.restPeriodEnd ?? undefined,
    note: email.note, restPeriodReasonId: email.restPeriodReasonId, draft: email.restPeriodDraft,
  } : null, [email]);
  const canProcess = auth.hasAnyPermission(action === "cancellation" ? Permissions.ContractsCancel : Permissions.RestPeriodsWrite);
  const existingReference = email?.effectiveRelevance !== RELEVANCE_RELEVANT || email.processingStatus === PROCESSING_STATUS_QUEUED || email.processingStatus === PROCESSING_STATUS_PROCESSING ? null :
    action === "rest_period" ? email?.existingRestPeriodIds[0] :
    loaded?.contract?.cancellationEffectiveDate &&
      (loaded.contract.status === CONTRACT_STATUS_CANCELLED || loaded.contract.status === CONTRACT_STATUS_ENDED)
      ? loaded.contract.id : null;
  const ready = email && loaded.person && loaded.contract && canProcess && !completed &&
    !existingReference &&
    (action !== "rest_period" || !!email.restPeriodStart && !!email.restPeriodEnd) &&
    email.effectiveRelevance === RELEVANCE_RELEVANT && email.effectiveIntentTypes.includes(action) &&
    email.processingStatus !== PROCESSING_STATUS_QUEUED && email.processingStatus !== PROCESSING_STATUS_PROCESSING;
  // Zuordnung offen: dieselbe Mail-Ansicht wie im Posteingang (Zuordnen, dann „Kündigung bearbeiten“) statt eines
  // fast leeren Hinweis-Drawers. Erledigte und mit einer bestehenden Änderung verknüpfbare Anfragen bleiben hier.
  const needsAssignment = !!email && canProcess && !completed && !existingReference && !ready;
  React.useEffect(() => {
    if (!needsAssignment || !email) return;
    onClose();
    navigate(`/communication/inbox?emailId=${encodeURIComponent(email.id)}`);
  }, [needsAssignment, email, navigate, onClose]);
  const original = email ? (
    <Collapse ghost items={[{ key: "source", label: text("Original message"), children: (
      <Space direction="vertical" style={{ width: "100%" }}>
        <DetailList>
          <DetailField label={text("Person")}>{email.personDisplayName || "—"}</DetailField>
          <DetailField label={text("From")}>{email.fromEmail}</DetailField>
          <DetailField label={text("Received at")}>{formatDateTimeNoSeconds(email.receivedAt, units)}</DetailField>
          <DetailField label={text("Subject")}>{email.subject}</DetailField>
        </DetailList>
        <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>{email.bodyText}</Typography.Paragraph>
        {email.attachments.map(attachment => (
          <Button key={attachment.id} type="link" onClick={() => {
            setAttachmentError(null);
            return auth.api.blob(`/api/v1/inbound-emails/${encodeURIComponent(email.id)}/attachments/${encodeURIComponent(attachment.id)}`)
              .then(blob => downloadBlob(blob, attachment.fileName)).catch(() => setAttachmentError(attachment.fileName));
          }}>{attachment.fileName}</Button>
        ))}
        {attachmentError ? <Alert type="error" message={text("Download failed")} description={attachmentError} /> : null}
      </Space>
    ) }]} />
  ) : null;
  if (!ready) return (
    <FormDrawer mode="view" dirty={false}  title={action === "cancellation" ? "Cancellation" : "Rest period"} open onCancel={onClose}  autoFocusFirstField={false} contentLoading={!loaded && !failed}>
      {failed ? <Alert type="error" message={text("Error loading data")} /> : completed ? (
        <Space direction="vertical">
          <Typography.Text>{text("Request processed")}</Typography.Text>
          <Typography.Text>{formatDateTimeNoSeconds(completed.completedAt, units)}</Typography.Text>
          <Button onClick={() => navigate(`/persons/${encodeURIComponent(completed.personId)}?tab=contracts`)}>{text("To person")}</Button>
          {original}
        </Space>
      ) : existingReference && canProcess && email ? (
        <Space direction="vertical">
          <Alert type="info" message={text("This request can be linked to an existing contract change.")} />
          <DetailList>
            <DetailField label={text("Person")}>{email.personDisplayName || "—"}</DetailField>
            <DetailField label={text("Contract")}>{email.contractPackageDisplayName || "—"}</DetailField>
            {action === "cancellation" ? <DetailField label={text("Membership end date")}>
              {formatDateOnly(loaded?.contract?.endDate || loaded?.contract?.cancellationEffectiveDate || "", units)}
            </DetailField> : <DetailField label={text("Rest period")}>
              {formatDateOnly(email.restPeriodStart || "", units)} – {formatDateOnly(email.restPeriodEnd || "", units)}
            </DetailField>}
          </DetailList>
          {original}
          {linkFailed ? <Alert type="error" message={text("Save failed")} /> : null}
          <Button type="primary" loading={linking} onClick={() => {
            setLinkFailed(false);
            setLinking(true);
            return auth.api.post(`/api/v1/inbound-emails/${encodeURIComponent(email.id)}/contract-processing`, {
              action: action === "cancellation" ? 0 : 1, referenceId: existingReference, expectedUpdatedAt: email.updatedAt,
            }).then(() => { onSuccess(); onClose(); }).catch(() => setLinkFailed(true)).finally(() => setLinking(false));
          }}>{text(action === "cancellation" ? "Link existing cancellation" : "Link existing rest period")}</Button>
        </Space>
      ) : (
        <Space direction="vertical">
          {email ? <Alert type="warning" message={text(canProcess ? "Check the email assignment before processing." : "Access denied")} /> : null}
          {original}
          {email ? <Button onClick={() => { onClose(); return navigate(`/communication/inbox?emailId=${encodeURIComponent(email.id)}`); }}>{text("Check email assignment")}</Button> : null}
        </Space>
      )}
    </FormDrawer>
  );
  const source = { id: email.id, updatedAt: email.updatedAt };
  return action === "cancellation" ? (
    <CancelContractDrawer open onClose={onClose} personId={loaded.person!.id} person={loaded.person}
      contractPackage={loaded.contract} inboundEmail={source} requestReceivedAt={email.receivedAt}
      requestedEffectiveDate={email.requestedCancellationDate} requestNote={email.note}
      requestReasonId={email.cancellationReasonId} requestContext={original} onSuccess={onSuccess}
      onOpenDocumentReview={onOpenDocumentReview} />
  ) : (
    <RestPeriodDrawer open onClose={onClose} personId={loaded.person!.id} personEmail={loaded.person!.email}
      context={{ personName: email.personDisplayName, contractPackageName: email.contractPackageDisplayName }}
      inboundEmail={source} requestContext={original} onSuccess={onSuccess}
      prefill={restPrefill} onOpenDocumentReview={onOpenDocumentReview} />
  );
}
